import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, FormControl, InputLabel, Select,
  MenuItem, Grid, CircularProgress, Tabs, Tab, Typography, IconButton, Autocomplete, Snackbar, Alert, Paper, Chip,
  FormControlLabel, Checkbox, Tooltip
} from '@mui/material';
import {
  Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon, SaveOutlined as SaveIcon, CheckCircleOutline as CheckIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';

import { useAccountService } from '../../services/accountService';
import { useCatalogService } from '../../services/catalogService';
import { useReferralService } from '../../services/referralService';
import IntakeContextPanel from '../common/IntakeContextPanel';
import type { ReferralIntakeContext } from '../../types/referralIntakeContext';

// ✅ CONSTANTE DE LOCATION NUMBERS POR PRODUCTO
const LOCATION_NUMBERS = {
  'EAP': '1389982',
  'LegalConnect': '1402862',
  'FinancialConnect': '1402864',
  'HWB - Well-Being Coaching': '2058643',
  'HWB - Tobacco & Nicotine Cessation': '2084868',
  'HWB - Weight Management': '2084868'
} as const;

// ✅ FUNCIÓN HELPER PARA OBTENER LOCATION NUMBER
const getLocationNumber = (product: string): string => {
  return LOCATION_NUMBERS[product as keyof typeof LOCATION_NUMBERS] || '1389982';
};

// ✅ FUNCIÓN PARA MAPEAR PRODUCTO A CATALOG_TYPE
const getSubCategoryCatalogType = (product: string): string | null => {
  switch (product) {
    case 'LegalConnect': return 'SUB_CATEGORY_LEGAL';
    case 'FinancialConnect': return 'SUB_CATEGORY_FINANCIAL';
    case 'HWB - Well-Being Coaching': return 'SUB_CATEGORY_HWB_COACHING';
    case 'Other': return 'SUB_CATEGORY_OTHER';
    default: return null; // EAP, HWB Tobacco/Weight no tienen subcategoría
  }
};

const shouldShowSubCategory = (product: string): boolean => {
  return getSubCategoryCatalogType(product) !== null;
};

// ✅ FUNCIÓN PARA MAPEAR PRODUCTO A CATALOG_TYPE DE PRESENTING_ISSUE  
const getPresentingIssueCatalogType = (product: string): string => {
  switch (product) {
    case 'EAP': return 'PRESENTING_ISSUE_EAP';
    case 'LegalConnect': return 'PRESENTING_ISSUE_LEGAL';
    case 'FinancialConnect': return 'PRESENTING_ISSUE_FINANCIAL';
    case 'HWB - Tobacco & Nicotine Cessation':
    case 'HWB - Weight Management':
    case 'HWB - Well-Being Coaching':
      return 'PRESENTING_ISSUE_HWB';
    case 'Other': return 'PRESENTING_ISSUE_EAP'; // Usar EAP como fallback
    default: return 'PRESENTING_ISSUE_EAP';
  }
};

// ✅ FUNCIÓN PARA DETERMINAR SI UN PRODUCTO PUEDE TENER CLAIMS
const canHaveClaims = (product: string): boolean => {
  return ['EAP', 'HWB - Tobacco & Nicotine Cessation', 'HWB - Weight Management', 'HWB - Well-Being Coaching'].includes(product);
};

// ✅ CONSTANTES DE STATUS
const REFERRAL_STATUS = {
  PENDING: 'PENDIENTE',
  SYNCHRONIZED: 'SINCRONIZADO',
  ERROR: 'ERROR',
  NO_APPOINTMENTS: 'SIN_CITAS'
} as const;

// ✅ FUNCIÓN PARA MAPEAR STATUS A DISPLAY
const getStatusDisplay = (status: string) => {
  switch (status) {
    case 'SINCRONIZADO':
      return { label: 'Sincronizado', color: 'success' as const, icon: '✅' };
    case 'SIN_CITAS':
      return { label: 'Sin citas', color: 'warning' as const, icon: '📋' };
    case 'PENDIENTE':
      return { label: 'Pendiente', color: 'info' as const, icon: '⏳' };
    case 'ERROR':
      return { label: 'Error', color: 'error' as const, icon: '❌' };
    default:
      return { label: status, color: 'default' as const, icon: '❓' };
  }
};

// ✅ FUNCIÓN PARA DETERMINAR STATUS ESPERADO
const determineExpectedStatus = (sendToComPsych: boolean, hasClaims: boolean): string => {
  if (!sendToComPsych) {
    return REFERRAL_STATUS.PENDING;
  }

  if (hasClaims) {
    return REFERRAL_STATUS.SYNCHRONIZED;
  }

  return REFERRAL_STATUS.NO_APPOINTMENTS;
};

// ✅ UTILIDADES DE SESIONES
const canAddClaim = (
  sessionsAuthorized: number,
  sessionsRemaining: number,
  currentClaimsCount: number,
  isEditMode: boolean = false,
  isSyncedWithComPsych: boolean = false  // ✅ NUEVO PARÁMETRO
): { canAdd: boolean; reason?: string } => {

  if (!isEditMode) {
    // Modo creación: validar contra sesiones autorizadas
    if (currentClaimsCount >= sessionsAuthorized) {
      return {
        canAdd: false,
        reason: `Límite alcanzado: ${sessionsAuthorized} sesiones autorizadas`
      };
    }
  } else {
    // ✅ LÓGICA CORREGIDA PARA MODO EDICIÓN
    if (isSyncedWithComPsych) {
      // Referral sincronizado: usar sessionsRemaining (lógica original)
      if (sessionsRemaining <= 0) {
        return {
          canAdd: false,
          reason: `Sin sesiones disponibles`
        };
      }
    } else {
      // ✅ Referral NO sincronizado: validar contra total de claims vs sessionsAuthorized
      if (currentClaimsCount >= sessionsAuthorized) {
        return {
          canAdd: false,
          reason: `Límite alcanzado: ${sessionsAuthorized} sesiones autorizadas`
        };
      }
    }
  }

  return { canAdd: true };
};

const validateBeforeSync = (
  claimsToSync: number,
  sessionsRemaining: number
): { canSync: boolean; message?: string } => {

  if (claimsToSync === 0) {
    return {
      canSync: false,
      message: 'No hay citas nuevas para sincronizar'
    };
  }

  if (claimsToSync > sessionsRemaining) {
    return {
      canSync: false,
      message: `No se pueden sincronizar ${claimsToSync} citas. Solo ${sessionsRemaining} sesiones disponibles.`
    };
  }

  return { canSync: true };
};

interface CaptureFormData {
  clientId: number | null;
  accountId: number | '';
  accountPathName: string;
  firstName: string;
  lastName: string;
  gender: string;
  employeeRelationship: string;
  countryAbbreviation: string;
  lengthOfService: string;
  ageRange: string;
  referralNumber: number | null;
  locationNumber: string;
  product: string;
  subCategory: string | null;
  deliveryMethod: string;
  presentingIssue: string;
  riskType: string;
  employeeType: string;
  referralSource: string;
  specialIncident: string | null;
  callDate: Date;
  sendToComPsych: boolean;
  sessionsAuthorized?: number;  // ✅ NUEVO
  sessionsRemaining?: number;   // ✅ NUEVO
  closeCase?: boolean;          // Checkbox: indicar que se quiere cerrar el caso
  closedReason?: string;        // Razón de cierre (catálogo CLOSED_REASON)
  claims: {
    id?: number;
    externalClaimId?: string;
    dateOfServiceFrom: Date;
    deliveryMethod: string;
    status?: string;
    readyForSync?: boolean; // ✅ NUEVO: Indica si la claim está lista para sincronizar
  }[];
}

interface CaptureModalProps {
  open: boolean;
  onClose: (refreshData?: boolean) => void;
  editRecord?: any;
  viewOnly?: boolean;
}

function TabPanel(props: { children?: React.ReactNode; value: string; tabId: string; }) {
  const { children, value, tabId, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== tabId}
      id={`capture-tabpanel-${tabId}`}
      aria-labelledby={`capture-tab-${tabId}`}
      {...other}
    >
      {value === tabId && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

type CaptureTabId = 'intake' | 'client' | 'case' | 'claims';

const initialForm: CaptureFormData = {
  clientId: null,
  accountId: '',
  accountPathName: '',
  firstName: '',
  lastName: '',
  gender: '',
  employeeRelationship: '',
  countryAbbreviation: 'MX',
  lengthOfService: '',
  ageRange: '',
  referralNumber: null,
  locationNumber: getLocationNumber('EAP'), // ✅ EAP por defecto
  product: 'EAP',
  subCategory: null,
  deliveryMethod: 'Face to Face',
  presentingIssue: '',
  riskType: 'No Risk',
  employeeType: 'Local-National',
  referralSource: '',
  specialIncident: null,
  callDate: new Date(),
  sendToComPsych: true,
  sessionsAuthorized: 0,  // ✅ NUEVO
  sessionsRemaining: 0,   // ✅ NUEVO
  closeCase: false,
  closedReason: '',
  claims: []
};

const CaptureModal: React.FC<CaptureModalProps> = ({ open, onClose, editRecord = null, viewOnly = false }) => {
  const [tabValue, setTabValue] = useState<CaptureTabId>('client');
  const [formData, setFormData] = useState<CaptureFormData>(initialForm);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [referralStatus, setReferralStatus] = useState<string | null>(null);
  const [isSyncedWithComPsych, setIsSyncedWithComPsych] = useState<boolean>(false);
  const [hasNewClaims, setHasNewClaims] = useState<boolean>(false);
  const [selectedAccountSessions, setSelectedAccountSessions] = useState<number>(0); // ✅ NUEVO
  const [deletedClaims, setDeletedClaims] = useState<number[]>([]); // ✅ NUEVO

  const [loading, setLoading] = useState(false);
  const [submitSuccessful, setSubmitSuccessful] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'success' | 'warning' | 'error' });
  const [accounts, setAccounts] = useState<any[]>([]);
  const [catalogs, setCatalogs] = useState<{ [key: string]: any }>({});
  const [catalogLoading, setCatalogLoading] = useState<boolean>(false);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [editDataLoaded, setEditDataLoaded] = useState<boolean>(false);
  const [sendToComPsych, setSendToComPsych] = useState<boolean>(true);
  const [referralOrigin, setReferralOrigin] = useState<string | null>(null);
  const [intakeContext, setIntakeContext] = useState<ReferralIntakeContext | null>(null);
  const [intakeContextLoading, setIntakeContextLoading] = useState(false);
  const [intakeReferralSource, setIntakeReferralSource] = useState('');
  const [intakeComments, setIntakeComments] = useState('');
  const [employeeRelationshipDescription, setEmployeeRelationshipDescription] = useState('');

  // ✅ CAMBIO 1: ELIMINAR el auto-disable problemático del sendToComPsych
  // ❌ ELIMINADO: useEffect que forzaba setSendToComPsych(false) cuando no hay claims

  const { getAvailableAccounts } = useAccountService();
  const { getAllCatalogs } = useCatalogService();
  const { createReferral, updateReferral, getReferralById, getIntakeContext } = useReferralService();
  const showIntakeTab = referralOrigin === 'PHONE_CALL';

  const tabOrder = useMemo<CaptureTabId[]>(() => {
    const tabs: CaptureTabId[] = [];
    if (showIntakeTab) tabs.push('intake');
    tabs.push('client', 'case');
    if (canHaveClaims(formData.product)) tabs.push('claims');
    return tabs;
  }, [showIntakeTab, formData.product]);

  const tabNumber = (tabId: CaptureTabId) => tabOrder.indexOf(tabId) + 1;

  const isFirstTab = tabOrder.indexOf(tabValue) === 0;
  const isLastTab = tabOrder.indexOf(tabValue) === tabOrder.length - 1;

  useEffect(() => {
    if (!tabOrder.includes(tabValue)) {
      setTabValue(tabOrder[0] ?? 'client');
    }
  }, [tabOrder, tabValue]);

  // ✅ FUNCIÓN: Verificar si una cita específica está sincronizada
  const isClaimSynced = (claim: any) => {
    return !!(claim.id && claim.externalClaimId && claim.status === 'SINCRONIZADO');
  };

  // ✅ FUNCIÓN: Formatear fechas para el backend con timezone CDMX
  const formatDateForBackend = (date: Date): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      console.error('Invalid date provided to formatDateForBackend:', date);
      return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });
    }

    const cdmxDate = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Mexico_City',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);

    console.log('🗓️ CDMX Date formatting:', {
      inputDate: date,
      inputLocal: date.toLocaleDateString(),
      cdmxFormatted: cdmxDate,
      userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      isCDMXTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone === 'America/Mexico_City'
    });

    return cdmxDate;
  };

  const parseDateFromBackend = (dateString: string | null | undefined): Date => {
  if (!dateString) {
    return new Date();
  }

  // Agregar hora del mediodía para evitar problemas de timezone
  const dateWithTime = dateString + 'T12:00:00';
  const parsedDate = new Date(dateWithTime);

  return parsedDate;
};

  // ✅ FUNCIÓN: Obtener ayer en timezone CDMX
  const getYesterdayInCDMX = (): Date => {
    const todayInCDMX = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Mexico_City',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date());

    const todayDate = new Date(todayInCDMX + 'T00:00:00');
    const yesterdayDate = new Date(todayDate);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);

    console.log('🗓️ Yesterday in CDMX:', {
      todayInCDMX,
      yesterdayFormatted: yesterdayDate.toLocaleDateString(),
      userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });

    return yesterdayDate;
  };

  // ✅ FUNCIÓN: Verificar timezone del usuario
  const checkUserTimezone = () => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isCDMX = userTimezone === 'America/Mexico_City';

    console.log('🌍 User timezone check:', {
      detected: userTimezone,
      isCDMX,
      warning: !isCDMX ? 'Usuario no está en timezone de CDMX' : 'OK'
    });

    if (!isCDMX) {
      console.warn('⚠️  ADVERTENCIA: El usuario no está en timezone de CDMX. Las fechas se convertirán automáticamente.');
    }

    return { userTimezone, isCDMX };
  };

  // ✅ FUNCIÓN para detectar si está sincronizado con ComPsych
  const checkSyncStatus = (referralData: any) => {
    const issynced = !!(referralData.externalReferralId && referralData.clientId);
    setIsSyncedWithComPsych(issynced);

    console.log('🔍 Sync status check:', {
      externalReferralId: referralData.externalReferralId,
      clientId: referralData.clientId,
      isSynced: issynced
    });

    return issynced;
  };

  // ✅ FUNCIÓN MEJORADA: Detectar si hay claims nuevas (sin sincronizar)
  const checkForNewClaims = () => {
    const newClaims = formData.claims.some(claim => !isClaimSynced(claim));
    setHasNewClaims(newClaims);

    console.log('🆕 New claims check:', {
      totalClaims: formData.claims.length,
      syncedClaims: formData.claims.filter(claim => isClaimSynced(claim)).length,
      newClaims: formData.claims.filter(claim => !isClaimSynced(claim)).length,
      hasNewClaims: newClaims
    });

    return newClaims;
  };

  // ✅ FUNCIÓN MEJORADA: Determinar si los campos deben estar deshabilitados
  const isFieldDisabled = (tabNumber: 1 | 2 | 3, claimIndex?: number) => {
    if (viewOnly) return true;

    if (isSyncedWithComPsych) {
      // Pestañas 1 y 2: siempre deshabilitadas si está sincronizado
      if (tabNumber === 1 || tabNumber === 2) {
        return true;
      }

      // Pestaña 3: verificar si la cita específica está sincronizada
      if (tabNumber === 3 && claimIndex !== undefined) {
        const claim = formData.claims[claimIndex];
        return isClaimSynced(claim);
      }
    }

    return false;
  };

  // ✅ FUNCIÓN NUEVA: Determinar si se puede eliminar una cita
  const canRemoveClaim = (claimIndex: number) => {
    if (viewOnly) return false;
    const claim = formData.claims[claimIndex];
    return !isClaimSynced(claim);
  };

  // ✅ CAMBIO 2: FUNCIÓN NUEVA: Validación mejorada antes del submit
  const validateIntakeBeforeComPsychSend = () => {
    if (!showIntakeTab || !sendToComPsych || isSyncedWithComPsych) {
      return true;
    }

    const consultationReason =
      intakeContext?.referral?.comments?.trim() ||
      intakeComments.trim() ||
      '';

    if (!consultationReason) {
      setSnackbar({
        open: true,
        message: 'No se capturó motivo de consulta en el intake telefónico. Revisa la pestaña "Contexto intake" antes de enviar a ComPsych.',
        severity: 'error'
      });
      setTabValue('intake');
      return false;
    }

    if (intakeContext?.hadCrisis && !(intakeContext.crisisNotes?.trim())) {
      setSnackbar({
        open: true,
        message: 'La llamada indica crisis pero no hay notas de crisis. Completa el intake antes de enviar a ComPsych.',
        severity: 'error'
      });
      setTabValue('intake');
      return false;
    }

    if (intakeContext?.privacyConsent === false) {
      setSnackbar({
        open: true,
        message: 'El empleado no aceptó el aviso de privacidad en el intake telefónico.',
        severity: 'error'
      });
      setTabValue('intake');
      return false;
    }

    return true;
  };

  const validateSubmission = () => {
    // Solo validar citas nuevas/editables
    const editableClaims = formData.claims.filter((_, index) => !isFieldDisabled(3, index));

    // ✅ CORREGIDO: Solo validar si es referral YA SINCRONIZADO y quiere agregar citas
    if (isSyncedWithComPsych && sendToComPsych && editableClaims.length === 0 && deletedClaims.length === 0) {
      setSnackbar({
        open: true,
        message: 'No hay citas nuevas para sincronizar. Agrega al menos una cita nueva.',
        severity: 'warning'
      });
      return false;
    }

    // ✅ NUEVA VALIDACIÓN: Verificar límite de sesiones antes de enviar
    if (isSyncedWithComPsych && hasNewClaims && sendToComPsych) {
      const claimsToSyncCount = formData.claims.filter(claim => !isClaimSynced(claim)).length;
      const { canSync, message } = validateBeforeSync(claimsToSyncCount, formData.sessionsRemaining || 0);

      if (!canSync) {
        setSnackbar({
          open: true,
          message: message || 'Error de validación de sesiones',
          severity: 'error'
        });
        return false;
      }
    }

    // Validar fechas solo en citas editables
    const invalidEditableClaims = editableClaims.filter(claim => {
      const todayInCDMX = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Mexico_City',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(new Date());

      const claimDateInCDMX = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Mexico_City',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(claim.dateOfServiceFrom);

      return new Date(claimDateInCDMX + 'T00:00:00') >= new Date(todayInCDMX + 'T00:00:00');
    });

    if (invalidEditableClaims.length > 0) {
      setSnackbar({
        open: true,
        message: 'Las fechas de servicio en las citas nuevas deben ser anteriores a HOY (hora de CDMX).',
        severity: 'error'
      });
      setTabValue('claims');
      return false;
    }

    if (!validateIntakeBeforeComPsychSend()) {
      return false;
    }

    return true;
  };

  // ✅ CAMBIO 3: FUNCIÓN para determinar el texto del botón - MEJORADA
  const getSubmitButtonText = () => {
    if (loading) return 'Procesando...';
    if (submitSuccessful) return 'Completado';

    const hasDeletionsOnly = deletedClaims.length > 0 && !hasNewClaims;

    if (isSyncedWithComPsych) {
      if (hasNewClaims) {
        return sendToComPsych
          ? 'Sincronizar Citas Nuevas'
          : 'Guardar Citas Localmente';
      } else if (hasDeletionsOnly) {
        return 'Actualizar Registro';
      } else {
        return 'Sin cambios';
      }
    } else {
      // ✅ CASO: Referral nuevo - describir claramente qué va a pasar
      const baseAction = isEditMode ? 'Actualizar' : 'Guardar';

      if (sendToComPsych) {
        if (formData.claims.length > 0) {
          return `${baseAction} y Sincronizar con Citas`; // SINCRONIZADO
        } else {
          return `${baseAction} y Enviar sin Citas`; // SIN_CITAS - ✅ ESTO DEBE FUNCIONAR
        }
      } else {
        return `${baseAction} como Pendiente`; // PENDIENTE
      }
    }
  };

  // ✅ FUNCIÓN para determinar si el botón debe estar deshabilitado
  const isSubmitDisabled = () => {
    if (loading || submitSuccessful || viewOnly) return true;

    if (isSyncedWithComPsych) {
      // ✅ Permitir guardar si hay cambios: nuevos claims o eliminaciones
      return !(hasNewClaims || deletedClaims.length > 0);
    }

    // Para casos no sincronizados, siempre permitir guardar
    return false;
  };

  // Función para cargar datos iniciales
  const loadInitialData = async () => {
    setCatalogLoading(true);
    try {
      const [accountsData, catalogsData] = await Promise.all([
        getAvailableAccounts(),
        getAllCatalogs()
      ]);

      setAccounts(accountsData || []);
      setCatalogs(catalogsData || {});
      setDataLoaded(true);

    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error al cargar catálogos y cuentas',
        severity: 'error'
      });
    } finally {
      setCatalogLoading(false);
    }
  };

  // ✅ FUNCIÓN CORREGIDA: Cargar datos del registro en modo edición
  const loadEditData = async (recordId: number) => {
    if (editDataLoaded) return;

    if (!dataLoaded || Object.keys(catalogs).length === 0) {
      return;
    }

    try {
      const referralData = await getReferralById(recordId);

      console.log('📥 Raw referralData from backend:', referralData);
      console.log('📥 Claims from backend:', referralData.claims);

      const mappedData: CaptureFormData = {
        clientId: referralData.id || null,
        accountId: referralData.orgId || '',
        accountPathName: referralData.accountPathName || '',
        firstName: referralData.firstName || '',
        lastName: referralData.lastName || '',
        gender: referralData.gender || '',
        employeeRelationship: referralData.employeeRelationship || '',
        countryAbbreviation: referralData.countryAbbreviation || 'MX',
        lengthOfService: referralData.lengthOfService || '',
        ageRange: referralData.ageRange || '',
        referralNumber: referralData.referralNumber || null,
        locationNumber: referralData.locationNumber || '1389982',
        product: referralData.product || 'EAP',
        subCategory: referralData.subCategory || null,
        deliveryMethod: referralData.deliveryMethod || 'Face to Face',
        presentingIssue: referralData.presentingIssue || '',
        riskType: referralData.riskType || 'No Risk',
        employeeType: referralData.employeeType || 'Local-National',
        referralSource: referralData.referralSource || '',
        specialIncident: referralData.specialIncident || null,
        //callDate: referralData.callDate ? new Date(referralData.callDate) : new Date(),
        callDate: parseDateFromBackend(referralData.callDate),
        sendToComPsych: true,
        sessionsAuthorized: referralData.sessionsAuthorized || 0,  // ✅ NUEVO
        sessionsRemaining: referralData.sessionsRemaining || 0,    // ✅ NUEVO
        closeCase: !!referralData.closeCase,
        closedReason: referralData.closedReason || '',
        claims: referralData.claims?.map((claim: any) => ({
          id: claim.id,
          externalClaimId: claim.externalClaimId,
          //dateOfServiceFrom: claim.dateOfServiceFrom ? new Date(claim.dateOfServiceFrom) : new Date(),
          dateOfServiceFrom: parseDateFromBackend(claim.dateOfServiceFrom),
          deliveryMethod: claim.deliveryMethod || '',
          status: claim.status,
          readyForSync: claim.readyForSync || false // ✅ NUEVO: Cargar readyForSync desde el backend
        })) || []
      };

      if (
        mappedData.accountId &&
        !accounts.some(account => account.orgId === mappedData.accountId)
      ) {
        setAccounts(currentAccounts => [
          ...currentAccounts,
          {
            orgId: mappedData.accountId,
            orgPath: `${mappedData.accountPathName || mappedData.accountId} (Inactiva)`,
            isActive: false
          }
        ]);
      }

      console.log('📤 Mapped claims:', mappedData.claims);

      setFormData(mappedData);
      setReferralStatus(referralData.status || null);
      setReferralOrigin(referralData.origin || null);
      setIntakeReferralSource(referralData.referralSourceIntake || '');
      setIntakeComments(referralData.comments || '');
      setEmployeeRelationshipDescription(referralData.employeeRelationshipDescription || '');

      if (referralData.origin === 'PHONE_CALL') {
        setIntakeContextLoading(true);
        try {
          const context = await getIntakeContext(recordId);
          setIntakeContext(context);
        } catch (error) {
          console.warn('No se pudo cargar el contexto del intake:', error);
          setIntakeContext(null);
        } finally {
          setIntakeContextLoading(false);
        }
      } else {
        setIntakeContext(null);
      }

      // ✅ NUEVO: Verificar si está sincronizado con ComPsych
      const synced = checkSyncStatus(referralData);

      // Si está sincronizado, deshabilitar envío automático
      if (synced) {
        setSendToComPsych(true);
      }

      setEditDataLoaded(true);

    } catch (error) {
      console.error('❌ Error in loadEditData:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar los datos del registro',
        severity: 'error'
      });
    }
  };

  // useEffect para cargar datos del registro
  useEffect(() => {
    if (isEditMode && editRecord?.id && dataLoaded && !editDataLoaded && Object.keys(catalogs).length > 0) {
      loadEditData(editRecord.id);
    }
  }, [isEditMode, dataLoaded, editDataLoaded, accounts.length, Object.keys(catalogs).length]);

  // useEffect principal
  useEffect(() => {
    if (open) {
      // ✅ Verificar timezone del usuario
      checkUserTimezone();

      setDataLoaded(false);
      setEditDataLoaded(false);
      setIsEditMode(!!editRecord);
      setTabValue('client');
      setSubmitSuccessful(false);
      setSendToComPsych(true);
      setReferralStatus(null);
      setIsSyncedWithComPsych(false);
      setHasNewClaims(false);
      setSelectedAccountSessions(0); // ✅ NUEVO
      setDeletedClaims([]); // ✅ NUEVO
      setReferralOrigin(null);
      setIntakeContext(null);
      setIntakeContextLoading(false);
      setIntakeReferralSource('');
      setIntakeComments('');
      setEmployeeRelationshipDescription('');

      if (editRecord && editRecord.id) {
        loadInitialData();
      } else {
        setFormData(initialForm);
        loadInitialData();
      }
    }
  }, [open, editRecord]);

  // ✅ NUEVO: useEffect para monitorear cambios en claims
  useEffect(() => {
    if (formData.claims.length > 0) {
      const anyNewClaims = formData.claims.some(claim => !claim.externalClaimId);
      setHasNewClaims(anyNewClaims);
    } else {
      setHasNewClaims(false);
    }
  }, [formData.claims]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: CaptureTabId) => setTabValue(newValue);

  // ✅ MODIFICADO: handleAccountChange PARA CAPTURAR SESIONES
  const handleAccountChange = (_event: any, newValue: any) => {
    if (viewOnly || isFieldDisabled(1)) return;

    if (newValue) {
      setFormData(prev => ({
        ...prev,
        accountId: newValue.orgId,
        accountPathName: newValue.orgPath,
        // ✅ NUEVO: Capturar sesiones de la cuenta seleccionada
        sessionsAuthorized: newValue.sessionCount || 0,
        sessionsRemaining: newValue.sessionCount || 0
      }));
      setSelectedAccountSessions(newValue.sessionCount || 0);
    } else {
      setFormData(prev => ({
        ...prev,
        accountId: '',
        accountPathName: '',
        sessionsAuthorized: 0,
        sessionsRemaining: 0
      }));
      setSelectedAccountSessions(0);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (viewOnly) return;

    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: any) => {
    if (viewOnly) return;

    setFormData(prev => ({
      ...prev,
      [name]: value,
      // ✅ ACTUALIZAR LOCATION NUMBER, SUBCATEGORY Y PRESENTING ISSUE CUANDO CAMBIE PRODUCTO
      ...(name === 'product' && {
        locationNumber: getLocationNumber(value),
        subCategory: shouldShowSubCategory(value) ? '' : null,
        presentingIssue: ''
      })
    }));
  };

  const handleDateChange = (name: string, date: Date | null) => {
    if (viewOnly) return;

    if (date) setFormData(prev => ({ ...prev, [name]: date }));
  };

  // ✅ CORREGIDO: Agregar claim con fecha de ayer como máximo en timezone CDMX + validación de sesiones
  const addClaim = () => {
    // ✅ NUEVO: Agregar readyForSync: false por defecto
    if (viewOnly) return;

    // ✅ NUEVA VALIDACIÓN: Verificar límite de sesiones antes de agregar
    const sessionsAuthorized = isEditMode ? formData.sessionsAuthorized || 0 : selectedAccountSessions;
    const sessionsRemaining = isEditMode ? formData.sessionsRemaining || 0 : selectedAccountSessions;
    const currentClaimsCount = formData.claims.length;

    const { canAdd, reason } = canAddClaim(sessionsAuthorized, sessionsRemaining, currentClaimsCount, isEditMode);

    if (!canAdd) {
      setSnackbar({
        open: true,
        message: reason || 'No se pueden agregar más citas',
        severity: 'warning'
      });
      return;
    }

    const yesterdayInCDMX = getYesterdayInCDMX();
    const newClaim = {
      dateOfServiceFrom: yesterdayInCDMX,
      deliveryMethod: formData.deliveryMethod || '',
      readyForSync: false // ✅ NUEVO: readyForSync por defecto false
    };
    setFormData(prev => ({ ...prev, claims: [...prev.claims, newClaim] }));
  };

  // ✅ FUNCIÓN MEJORADA: Handler para actualizar citas
  const updateClaim = (index: number, field: string, value: any) => {
    if (viewOnly || isFieldDisabled(3, index)) return;

    const updatedClaims = [...formData.claims];
    updatedClaims[index] = { ...updatedClaims[index], [field]: value };
    setFormData(prev => ({ ...prev, claims: updatedClaims }));
  };

  // ✅ FUNCIÓN MEJORADA: Handler para eliminar citas
  const removeClaim = (index: number) => {
    if (viewOnly || !canRemoveClaim(index)) return;

    const claimToRemove = formData.claims[index];
    if (claimToRemove.id && !claimToRemove.externalClaimId) {
      setDeletedClaims(prev => [...prev, claimToRemove.id!]);
    }

    setFormData(prev => ({
      ...prev,
      claims: prev.claims.filter((_, i) => i !== index)
    }));
  };

  // ✅ FUNCIÓN NUEVA: Renderizar item de cita con controles mejorados
  const renderClaimItem = (claim: any, idx: number) => {
    // ✅ CRITERIO CORRECTO: Solo externalClaimId determina si está sincronizada
    const isSynced = !!claim.externalClaimId;
    const isEditable = !isFieldDisabled(3, idx);

    return (
      <Paper
        key={idx}
        sx={{
          p: 2,
          my: 1,
          border: '1px solid',
          borderColor: isSynced ? 'success.300' : 'grey.300',
          backgroundColor: isSynced ? 'success.50' : 'background.paper',
          opacity: isSynced ? 0.9 : 1
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
              Cita #{idx + 1}
            </Typography>

            {/* ✅ MOSTRAR INFO DE SINCRONIZACIÓN */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                Estado:
              </Typography>
              {isSynced ? (
                <Chip
                  label="✅ Sincronizada"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ) : (
                <Chip
                  label={claim.id ? "📝 Guardada localmente" : "🆕 Nueva"}
                  size="small"
                  color={claim.id ? "info" : "warning"}
                  variant="outlined"
                />
              )}
            </Box>

            {/* ✅ MOSTRAR ID DE COMPSYCH SI EXISTE */}
            {claim.externalClaimId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  ID ComPsych:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    color: 'success.main',
                    backgroundColor: 'success.100',
                    padding: '2px 6px',
                    borderRadius: 0.5,
                    border: 1,
                    borderColor: 'success.300'
                  }}
                >
                  {claim.externalClaimId}
                </Typography>
              </Box>
            )}

            {/* ✅ MOSTRAR ID LOCAL SI EXISTE PERO NO ESTÁ SINCRONIZADA */}
            {claim.id && !claim.externalClaimId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  ID Local:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    color: 'info.main',
                    backgroundColor: 'info.100',
                    padding: '2px 6px',
                    borderRadius: 0.5,
                    border: 1,
                    borderColor: 'info.300'
                  }}
                >
                  {claim.id}
                </Typography>
              </Box>
            )}
          </Box>

          {!viewOnly && (
            <Tooltip title={
              !canRemoveClaim(idx)
                ? "No se puede eliminar una cita sincronizada con ComPsych"
                : "Eliminar cita"
            }>
              <span>
                <IconButton
                  color="error"
                  onClick={() => removeClaim(idx)}
                  size="small"
                  disabled={!canRemoveClaim(idx)}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <DatePicker
              label="Fecha de servicio"
              value={claim.dateOfServiceFrom}
              onChange={(date) => updateClaim(idx, 'dateOfServiceFrom', date)}
              maxDate={viewOnly ? undefined : getYesterdayInCDMX()}
              disabled={viewOnly || !isEditable}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: !viewOnly,
                  error: !viewOnly && isEditable && (() => {
                    const todayInCDMX = new Intl.DateTimeFormat('en-CA', {
                      timeZone: 'America/Mexico_City',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    }).format(new Date());

                    const claimDateInCDMX = new Intl.DateTimeFormat('en-CA', {
                      timeZone: 'America/Mexico_City',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    }).format(claim.dateOfServiceFrom);

                    return new Date(claimDateInCDMX + 'T00:00:00') >= new Date(todayInCDMX + 'T00:00:00');
                  })(),
                  helperText: !viewOnly && isEditable && (() => {
                    const todayInCDMX = new Intl.DateTimeFormat('en-CA', {
                      timeZone: 'America/Mexico_City',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    }).format(new Date());

                    const claimDateInCDMX = new Intl.DateTimeFormat('en-CA', {
                      timeZone: 'America/Mexico_City',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    }).format(claim.dateOfServiceFrom);

                    return new Date(claimDateInCDMX + 'T00:00:00') >= new Date(todayInCDMX + 'T00:00:00')
                      ? 'La fecha de servicio debe ser anterior a hoy'
                      : 'Selecciona una fecha anterior a hoy';
                  })(),
                  InputProps: {
                    style: !isEditable ? { backgroundColor: '#f5f5f5' } : {}
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth required={!viewOnly && isEditable}>
              <InputLabel>Método de Entrega</InputLabel>
              <Select
                value={claim.deliveryMethod}
                onChange={e => updateClaim(idx, 'deliveryMethod', e.target.value)}
                label="Método de Entrega"
                disabled={viewOnly || !isEditable}
                sx={!isEditable ? { backgroundColor: '#f5f5f5' } : {}}
              >
                {renderCatalogOptions('DELIVERY_METHOD')}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // ✅ CORREGIDO: handleSubmit con filtrado automático de claims sincronizadas
  const handleSubmit = async () => {
    if (isSubmitDisabled()) return;

    // Validación de campos requeridos (solo si no está sincronizado)
    if (!isSyncedWithComPsych) {
      const requiredFields = [
        formData.accountId, formData.firstName, formData.lastName, formData.gender, formData.employeeRelationship,
        formData.lengthOfService, formData.ageRange, formData.product, formData.presentingIssue,
        formData.riskType, formData.employeeType, formData.referralSource, formData.deliveryMethod, formData.callDate
      ];

      const allValid = requiredFields.every(f => f !== null && f !== '');
      if (!allValid) {
        setSnackbar({ open: true, message: 'Por favor llena todos los campos obligatorios.', severity: 'error' });
        return;
      }

      // ✅ VALIDACIÓN ESPECÍFICA DE SUBCATEGORÍA REQUERIDA
      if (shouldShowSubCategory(formData.product) && (!formData.subCategory || formData.subCategory.trim() === '')) {
        setSnackbar({
          open: true,
          message: `El producto "${formData.product}" requiere seleccionar una subcategoría.`,
          severity: 'error'
        });
        setTabValue('case');
        return;
      }
    }

    // ✅ USAR NUEVA VALIDACIÓN
    if (!validateSubmission()) {
      return;
    }

    try {
      setLoading(true);

      const { userTimezone, isCDMX } = checkUserTimezone();

      let expectedStatus;
      let actionMessage;

      // ✅ NUEVO: FILTRADO AUTOMÁTICO DE CLAIMS
      let claimsToSend;
      let onlyAddClaims = false;

      if (isSyncedWithComPsych) {
        // ✅ FILTRAR AUTOMÁTICAMENTE: Solo enviar claims sin externalClaimId
        claimsToSend = formData.claims.filter(claim => !claim.externalClaimId);
        onlyAddClaims = true;
        expectedStatus = 'SINCRONIZADO';
        actionMessage = `Agregando ${claimsToSend.length} citas nuevas al referral existente en ComPsych`;

        console.log('🔍 FILTRADO AUTOMÁTICO:', {
          totalClaims: formData.claims.length,
          syncedClaims: formData.claims.filter(claim => claim.externalClaimId).length,
          newClaimsToSend: claimsToSend.length,
          filteredOut: formData.claims.length - claimsToSend.length
        });

        // ✅ VALIDACIÓN: Si no hay claims nuevas, no enviar
        const realSendToComPsych = sendToComPsych && formData.claims.some(c => !c.externalClaimId);

        if (realSendToComPsych && claimsToSend.length === 0) {
          setSnackbar({
            open: true,
            message: 'No hay citas nuevas para sincronizar. Todas las citas ya están sincronizadas con ComPsych.',
            severity: 'warning'
          });
          setLoading(false);
          return;
        }

        // Captura manual: no requiere readyForSync (solo aplica a llamadas telefónicas)
      } else {
        // ✅ REFERRAL NO SINCRONIZADO: Enviar todas las claims
        claimsToSend = formData.claims;
        onlyAddClaims = false;
        expectedStatus = determineExpectedStatus(sendToComPsych, claimsToSend.length > 0);
        actionMessage = 'Procesando referral completo';

        // Captura manual: no requiere readyForSync (solo aplica a llamadas telefónicas)
      }

      console.log('📊 Submit action:', {
        isSyncedWithComPsych,
        totalClaims: formData.claims.length,
        claimsToSend: claimsToSend.length,
        onlyAddClaims,
        expectedStatus,
        actionMessage
      });

      // ✅ CONSTRUIR REQUEST CON CLAIMS FILTRADAS
      const hasNewClaims = formData.claims.some(claim => !claim.externalClaimId);
      const calculatedOnlyAddClaims = isSyncedWithComPsych && hasNewClaims;

      const dataToSend: any = {
        ...formData,
        callDate: formatDateForBackend(formData.callDate),
        claims: claimsToSend.map(claim => ({
          ...(claim.id && { id: claim.id }),
          dateOfServiceFrom: formatDateForBackend(claim.dateOfServiceFrom),
          deliveryMethod: claim.deliveryMethod,
          readyForSync: claim.readyForSync || false // ✅ NUEVO: Incluir readyForSync en el payload
        })),
        sendToComPsych: isSyncedWithComPsych ? sendToComPsych : sendToComPsych,
        expectedStatus: expectedStatus,
        onlyAddClaims: calculatedOnlyAddClaims,
        _timezone: userTimezone,
        _isCDMX: isCDMX,
        // Cerrar caso: se envía el code del catálogo CLOSED_REASON (no la descripción)
        closeCase: formData.closeCase ?? false,
        closedReason: formData.closeCase && formData.closedReason ? formData.closedReason : null
      };

      console.log('📤 Data to send (automatically filtered):', {
        onlyAddClaims: dataToSend.onlyAddClaims,
        claimsCount: dataToSend.claims.length,
        claimsData: dataToSend.claims
      });

      dataToSend.claimsToDelete = deletedClaims;
      let response;
      try {
        if (isEditMode && editRecord?.id) {
          response = await updateReferral(editRecord.id, dataToSend);
        } else {
          response = await createReferral(dataToSend);
        }
      } catch (apiError: any) {
        // ✅ NUEVO: Capturar error de la API y re-lanzar con información adicional
        if (apiError?.response) {
          // Si es un Response de fetch, intentar parsear el JSON
          const errorData = await apiError.response.json().catch(() => ({}));
          const errorWithData = new Error(errorData.message || apiError.message || 'Error al procesar la solicitud');
          (errorWithData as any).response = apiError.response;
          (errorWithData as any).status = apiError.response.status;
          throw errorWithData;
        }
        throw apiError;
      }

      setSubmitSuccessful(true);

      // ✅ MENSAJE DE CONFIRMACIÓN ESPECÍFICO
      const statusMessage = (() => {
        if (isSyncedWithComPsych) {
          if (sendToComPsych) {
            return `${claimsToSend.length} citas nuevas sincronizadas con ComPsych exitosamente`;
          } else {
            return `${claimsToSend.length} citas nuevas guardadas localmente (no enviadas a ComPsych)`;
          }
        }

        switch (expectedStatus) {
          case 'SINCRONIZADO':
            return 'Registro guardado y sincronizado con ComPsych exitosamente';
          case 'SIN_CITAS':
            return 'Registro enviado a ComPsych. Recuerda programar las citas posteriormente';
          case 'PENDIENTE':
            return 'Registro guardado. Se enviará a ComPsych cuando agregues citas';
          default:
            return response.message || 'Operación completada exitosamente';
        }
      })();

      setSnackbar({
        open: true,
        message: statusMessage,
        severity: 'success'
      });

      setDeletedClaims([]);
      setTimeout(() => {
        onClose(true);
      }, 1500);

    } catch (error: any) {
      console.error('Error:', error);
      
      // ✅ NUEVO: Manejar error 400 del backend cuando no hay claims listas para sincronizar
      let errorMessage = isEditMode ? 'Error al actualizar el registro' : 'Error al guardar el registro';
      
      // ✅ NUEVO: Manejar error 400 del backend cuando no hay claims listas para sincronizar
      // El error puede venir de diferentes formas dependiendo de cómo se maneje en el servicio
      if (error?.message) {
        if (error.message.includes('No claims ready to synchronize') || error.message.includes('no claims ready')) {
          errorMessage = error.message;
        } else {
          errorMessage = error.message;
        }
      } else if (error?.response) {
        // Si el error tiene una propiedad response (Response de fetch)
        try {
          const errorData = await error.response.json().catch(() => ({}));
          if (errorData.message) {
            if (errorData.message.includes('No claims ready to synchronize') || errorData.message.includes('no claims ready')) {
              errorMessage = errorData.message;
            } else {
              errorMessage = errorData.message;
            }
          }
        } catch (parseError) {
          // Si no se puede parsear el error, usar el mensaje por defecto
          console.error('Error parsing error response:', parseError);
        }
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderCatalogOptions = (key: string) => {
    if (!catalogs[key] || !Array.isArray(catalogs[key])) {
      return [];
    }

    return catalogs[key].map((item: any) => (
      <MenuItem key={item.code} value={item.code}>
        {item.description}
      </MenuItem>
    ));
  };

  // ✅ FUNCIÓN ESPECÍFICA PARA SUBCATEGORÍAS
  const renderSubCategoryOptions = (product: string) => {
    const catalogType = getSubCategoryCatalogType(product);
    if (!catalogType || !catalogs[catalogType] || !Array.isArray(catalogs[catalogType])) {
      return [];
    }

    return catalogs[catalogType].map((item: any) => (
      <MenuItem key={item.code} value={item.code}>
        {item.description}
      </MenuItem>
    ));
  };

  const nextTab = () => {
    const currentIndex = tabOrder.indexOf(tabValue);
    if (currentIndex < tabOrder.length - 1) {
      setTabValue(tabOrder[currentIndex + 1]);
    }
  };
  const prevTab = () => {
    const currentIndex = tabOrder.indexOf(tabValue);
    if (currentIndex > 0) {
      setTabValue(tabOrder[currentIndex - 1]);
    }
  };

  // UI - Loading state
  if (catalogLoading || !dataLoaded || (isEditMode && !editDataLoaded)) {
    return (
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Cargando datos del registro...' : 'Cargando catálogos...'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>
              {catalogLoading
                ? 'Cargando opciones disponibles...'
                : isEditMode && !editDataLoaded
                  ? 'Obteniendo información del registro...'
                  : 'Preparando formulario...'
              }
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Dialog
        open={open}
        onClose={() => onClose(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {viewOnly
                    ? 'Ver Registro'
                    : isEditMode
                      ? 'Editar Registro'
                      : 'Nueva Captura'
                  }
                </Typography>

                {(isEditMode || viewOnly) && referralStatus && (
                  <Chip
                    label={`${getStatusDisplay(referralStatus).icon} ${getStatusDisplay(referralStatus).label}`}
                    color={getStatusDisplay(referralStatus).color}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>

              <Typography variant="body2" color="text.secondary">
                {viewOnly
                  ? `Visualizando información del caso #${editRecord?.externalReferralId || editRecord?.id}`
                  : isEditMode
                    ? `Modificar información del caso #${editRecord?.externalReferralId || editRecord?.id}`
                    : 'Crear nuevo caso y/o citas'
                }
              </Typography>
            </Box>
            <IconButton aria-label="close" onClick={() => onClose(false)} sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="capture tabs" sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            {showIntakeTab && (
              <Tab
                value="intake"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tabNumber('intake')}. Contexto intake
                    {intakeContext && <CheckIcon fontSize="small" color="success" />}
                  </Box>
                }
              />
            )}
            <Tab
              value="client"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tabNumber('client')}. Cliente {formData.firstName && formData.lastName && formData.accountId &&
                    <CheckIcon fontSize="small" color="success" />}
                </Box>
              }
            />
            <Tab
              value="case"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tabNumber('case')}. Caso {formData.product && formData.presentingIssue &&
                    <CheckIcon fontSize="small" color="success" />}
                </Box>
              }
            />
            {canHaveClaims(formData.product) && (
              <Tab
                value="claims"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tabNumber('claims')}. Citas <Chip label={formData.claims.length} size="small" color="primary" />
                  </Box>
                }
              />
            )}
          </Tabs>

          {showIntakeTab && (
            <TabPanel value={tabValue} tabId="intake">
              <IntakeContextPanel context={intakeContext} loading={intakeContextLoading} />
            </TabPanel>
          )}

          {/* Cliente */}
          <TabPanel value={tabValue} tabId="client">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  key={`account-${formData.accountId}`}
                  options={Array.isArray(accounts) ? accounts : []}
                  getOptionLabel={(option) => option?.orgPath || ''}
                  getOptionDisabled={(option) => option?.isActive === false}
                  isOptionEqualToValue={(option, value) => option.orgId === value.orgId}
                  onChange={handleAccountChange}
                  value={
                    Array.isArray(accounts) && formData.accountId
                      ? accounts.find(acc => acc.orgId === formData.accountId) || null
                      : null
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required={!viewOnly && !isFieldDisabled(1)}
                      label="Cuenta / Organización"
                      placeholder="Seleccionar cuenta..."
                      error={!viewOnly && !isFieldDisabled(1) && !formData.accountId}
                      helperText={!formData.accountId ? 'Selecciona una cuenta' : formData.accountPathName}
                      InputProps={{
                        ...params.InputProps,
                        readOnly: viewOnly || isFieldDisabled(1),
                        style: isFieldDisabled(1) ? { backgroundColor: '#f5f5f5' } : {}
                      }}
                    />
                  )}
                  disabled={catalogLoading || accounts.length === 0 || viewOnly || isFieldDisabled(1)}
                />
              </Grid>

              {/* ✅ NUEVA SECCIÓN: Información de sesiones */}
              {formData.accountId && (
                <Grid item xs={12}>
                  <Paper sx={{
                    p: 2,
                    backgroundColor: 'info.50',
                    border: '1px solid',
                    borderColor: 'info.200',
                    borderRadius: 1
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="info.main" sx={{ fontWeight: 'bold' }}>
                        📋 Sesiones para este Caso:
                      </Typography>

                      {/* MODO CREACIÓN - Mostrar sesiones de la cuenta */}
                      {!isEditMode && (
                        <Chip
                          label={`${selectedAccountSessions} autorizadas`}
                          color="info"
                          size="small"
                          variant="outlined"
                        />
                      )}

                      {/* MODO EDICIÓN/VISTA - Mostrar sesiones del referral */}
                      {(isEditMode || viewOnly) && formData.sessionsAuthorized !== undefined && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label={`${formData.sessionsAuthorized} Autorizadas`}
                            color="primary"
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`${formData.sessionsRemaining || 0} Restantes`}
                            color={formData.sessionsRemaining && formData.sessionsRemaining > 0 ? "success" : "warning"}
                            size="small"
                          />
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              )}

              <Grid item xs={6}>
                <TextField
                  name="firstName"
                  label="Nombre"
                  required={!viewOnly && !isFieldDisabled(1)}
                  fullWidth
                  value={formData.firstName}
                  onChange={handleChange}
                  error={!viewOnly && !isFieldDisabled(1) && !formData.firstName}
                  InputProps={{
                    readOnly: viewOnly || isFieldDisabled(1),
                    style: isFieldDisabled(1) ? { backgroundColor: '#f5f5f5' } : {}
                  }}
                  helperText=""
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="lastName"
                  label="Apellido"
                  required={!viewOnly && !isFieldDisabled(1)}
                  fullWidth
                  value={formData.lastName}
                  onChange={handleChange}
                  error={!viewOnly && !isFieldDisabled(1) && !formData.lastName}
                  InputProps={{
                    readOnly: viewOnly || isFieldDisabled(1),
                    style: isFieldDisabled(1) ? { backgroundColor: '#f5f5f5' } : {}
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required={!viewOnly && !isFieldDisabled(1)} error={!viewOnly && !isFieldDisabled(1) && !formData.gender}>
                  <InputLabel>Género</InputLabel>
                  <Select
                    value={formData.gender}
                    onChange={e => handleSelectChange('gender', e.target.value)}
                    label="Género"
                    disabled={viewOnly || isFieldDisabled(1)}
                    sx={isFieldDisabled(1) ? { backgroundColor: '#f5f5f5' } : {}}
                  >
                    {renderCatalogOptions('GENDER')}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required={!viewOnly && !isFieldDisabled(1)} error={!viewOnly && !isFieldDisabled(1) && !formData.employeeRelationship}>
                  <InputLabel>Relación</InputLabel>
                  <Select
                    value={formData.employeeRelationship}
                    onChange={e => handleSelectChange('employeeRelationship', e.target.value)}
                    label="Relación"
                    disabled={viewOnly || isFieldDisabled(1)}
                    sx={isFieldDisabled(1) ? { backgroundColor: '#f5f5f5' } : {}}
                  >
                    {renderCatalogOptions('EMPLOYEE_RELATIONSHIP')}
                  </Select>
                </FormControl>
              </Grid>
              {employeeRelationshipDescription && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción de relación (intake)"
                    value={employeeRelationshipDescription}
                    InputProps={{ readOnly: true }}
                    multiline
                    minRows={1}
                    sx={{ '& .MuiInputBase-input': { backgroundColor: '#f5f5f5' } }}
                  />
                </Grid>
              )}
              <Grid item xs={6}>
                <FormControl fullWidth required={!viewOnly && !isFieldDisabled(1)} error={!viewOnly && !isFieldDisabled(1) && !formData.lengthOfService}>
                  <InputLabel>Tiempo de Servicio</InputLabel>
                  <Select
                    key={`lengthOfService-${formData.lengthOfService}`}
                    value={formData.lengthOfService}
                    onChange={e => handleSelectChange('lengthOfService', e.target.value)}
                    label="Tiempo de Servicio"
                    disabled={viewOnly || isFieldDisabled(1)}
                    sx={isFieldDisabled(1) ? { backgroundColor: '#f5f5f5' } : {}}
                  >
                    {renderCatalogOptions('LENGTH_OF_SERVICE')}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required={!viewOnly && !isFieldDisabled(1)} error={!viewOnly && !isFieldDisabled(1) && !formData.ageRange}>
                  <InputLabel>Rango de Edad</InputLabel>
                  <Select
                    value={formData.ageRange}
                    onChange={e => handleSelectChange('ageRange', e.target.value)}
                    label="Rango de Edad"
                    disabled={viewOnly || isFieldDisabled(1)}
                    sx={isFieldDisabled(1) ? { backgroundColor: '#f5f5f5' } : {}}
                  >
                    {renderCatalogOptions('AGE_RANGE')}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Caso */}
          <TabPanel value={tabValue} tabId="case">
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <FormControl fullWidth required={!viewOnly && !isFieldDisabled(2)} error={!viewOnly && !isFieldDisabled(2) && !formData.product}>
                  <InputLabel>Producto</InputLabel>
                  <Select
                    value={formData.product}
                    onChange={e => handleSelectChange('product', e.target.value)}
                    label="Producto"
                    disabled={viewOnly || isFieldDisabled(2)}
                    sx={isFieldDisabled(2) ? { backgroundColor: '#f5f5f5' } : {}}
                  >
                    {renderCatalogOptions('PRODUCT')}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                {shouldShowSubCategory(formData.product) ? (
                  <FormControl fullWidth required={!viewOnly && !isFieldDisabled(2)} error={!viewOnly && !isFieldDisabled(2) && !formData.subCategory}>
                    <InputLabel>Subcategoría</InputLabel>
                    <Select
                      value={formData.subCategory || ''}
                      onChange={e => handleSelectChange('subCategory', e.target.value)}
                      label="Subcategoría"
                      disabled={viewOnly || isFieldDisabled(2)}
                      sx={isFieldDisabled(2) ? { backgroundColor: '#f5f5f5' } : {}}
                    >
                      {renderSubCategoryOptions(formData.product)}
                    </Select>
                  </FormControl>
                ) : <Box />}
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="Fecha de llamada"
                  value={formData.callDate}
                  onChange={date => handleDateChange('callDate', date)}
                  disabled={viewOnly || isFieldDisabled(2)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: !viewOnly && !isFieldDisabled(2),
                      InputProps: {
                        style: isFieldDisabled(2) ? { backgroundColor: '#f5f5f5' } : {}
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required={!viewOnly && !isFieldDisabled(2)} error={!viewOnly && !isFieldDisabled(2) && !formData.presentingIssue}>
                  <InputLabel>Problema Presentado</InputLabel>
                  <Select
                    value={formData.presentingIssue}
                    onChange={e => handleSelectChange('presentingIssue', e.target.value)}
                    label="Problema Presentado"
                    disabled={viewOnly || isFieldDisabled(2)}
                    sx={isFieldDisabled(2) ? { backgroundColor: '#f5f5f5' } : {}}
                  >
                    {renderCatalogOptions(getPresentingIssueCatalogType(formData.product))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required={!viewOnly && !isFieldDisabled(2)} error={!viewOnly && !isFieldDisabled(2) && !formData.riskType}>
                  <InputLabel>Tipo de Riesgo</InputLabel>
                  <Select
                    value={formData.riskType}
                    onChange={e => handleSelectChange('riskType', e.target.value)}
                    label="Tipo de Riesgo"
                    disabled={viewOnly || isFieldDisabled(2)}
                    sx={isFieldDisabled(2) ? { backgroundColor: '#f5f5f5' } : {}}
                  >
                    {renderCatalogOptions('RISK_TYPE')}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required={!viewOnly && !isFieldDisabled(2)} error={!viewOnly && !isFieldDisabled(2) && !formData.employeeType}>
                  <InputLabel>Tipo de Empleado</InputLabel>
                  <Select
                    value={formData.employeeType}
                    onChange={e => handleSelectChange('employeeType', e.target.value)}
                    label="Tipo de Empleado"
                    disabled={viewOnly || isFieldDisabled(2)}
                    sx={isFieldDisabled(2) ? { backgroundColor: '#f5f5f5' } : {}}
                  >
                    {renderCatalogOptions('EMPLOYEE_TYPE')}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required={!viewOnly && !isFieldDisabled(2)} error={!viewOnly && !isFieldDisabled(2) && !formData.referralSource}>
                  <InputLabel>Origen</InputLabel>
                  <Select
                    value={formData.referralSource}
                    onChange={e => handleSelectChange('referralSource', e.target.value)}
                    label="Origen"
                    disabled={viewOnly || isFieldDisabled(2)}
                    sx={isFieldDisabled(2) ? { backgroundColor: '#f5f5f5' } : {}}
                  >
                    {renderCatalogOptions('REFERRAL_SOURCE')}
                  </Select>
                </FormControl>
              </Grid>
              {intakeReferralSource && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="¿Cómo se enteró? (intake)"
                    value={intakeReferralSource}
                    InputProps={{ readOnly: true }}
                    multiline
                    minRows={1}
                    sx={{ '& .MuiInputBase-input': { backgroundColor: '#f5f5f5' } }}
                  />
                </Grid>
              )}
              {intakeComments && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notas del caso (intake)"
                    value={intakeComments}
                    InputProps={{ readOnly: true }}
                    multiline
                    minRows={2}
                    sx={{ '& .MuiInputBase-input': { backgroundColor: '#f5f5f5' } }}
                  />
                </Grid>
              )}
              <Grid item xs={6}>
                <FormControl fullWidth required={!viewOnly && !isFieldDisabled(2)} error={!viewOnly && !isFieldDisabled(2) && !formData.deliveryMethod}>
                  <InputLabel>Método de Entrega</InputLabel>
                  <Select
                    value={formData.deliveryMethod}
                    onChange={e => handleSelectChange('deliveryMethod', e.target.value)}
                    label="Método de Entrega"
                    disabled={viewOnly || isFieldDisabled(2)}
                    sx={isFieldDisabled(2) ? { backgroundColor: '#f5f5f5' } : {}}
                  >
                    {renderCatalogOptions('DELIVERY_METHOD')}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth error={false}>
                  <InputLabel>Incidente Especial</InputLabel>
                  <Select
                    value={formData.specialIncident || ''}
                    onChange={e => handleSelectChange('specialIncident', e.target.value || null)}
                    label="Incidente Especial"
                    disabled={viewOnly || isFieldDisabled(2)}
                    sx={isFieldDisabled(2) ? { backgroundColor: '#f5f5f5' } : {}}
                  >
                    <MenuItem value="">
                      <em>Ninguno</em>
                    </MenuItem>
                    {renderCatalogOptions('SPECIAL_INCIDENT')}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.closeCase ?? false}
                      onChange={e => {
                        const checked = (e.target as HTMLInputElement).checked;
                        setFormData(prev => ({
                          ...prev,
                          closeCase: checked,
                          closedReason: checked ? prev.closedReason : ''
                        }));
                      }}
                      disabled={viewOnly || isFieldDisabled(2)}
                    />
                  }
                  label="Cerrar el caso"
                />
              </Grid>
              {formData.closeCase && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required={!viewOnly && !isFieldDisabled(2)}>
                    <InputLabel>Razón de cierre</InputLabel>
                    <Select
                      value={formData.closedReason ?? ''}
                      onChange={e => handleSelectChange('closedReason', e.target.value)}
                      label="Razón de cierre"
                      disabled={viewOnly || isFieldDisabled(2)}
                      sx={isFieldDisabled(2) ? { backgroundColor: '#f5f5f5' } : {}}
                    >
                      <MenuItem value="">
                        <em>Seleccione razón de cierre</em>
                      </MenuItem>
                      {renderCatalogOptions('CLOSED_REASON')}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* ✅ Tab 3: Citas MEJORADO con validaciones en timezone CDMX */}
          {canHaveClaims(formData.product) && (
            <TabPanel value={tabValue} tabId="claims">
              <Box>
                {/* ✅ HEADER CON INFORMACIÓN DE SESIONES */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Citas Programadas</Typography>

                  {/* ✅ MOSTRAR INFO DE SESIONES EN MODO EDICIÓN/VISTA */}
                  {(isEditMode || viewOnly) && formData.sessionsAuthorized !== undefined && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {formData.sessionsAuthorized} autorizadas
                      </Typography>
                      <Typography variant="body2" color="text.secondary">•</Typography>
                      <Typography
                        variant="body2"
                        color={formData.sessionsRemaining && formData.sessionsRemaining > 0 ? "success.main" : "warning.main"}
                        sx={{ fontWeight: 'medium' }}
                      >
                        {formData.sessionsRemaining || 0} restantes
                      </Typography>
                    </Box>
                  )}

                  {/* MODO CREACIÓN - Mostrar progreso simple */}
                  {!isEditMode && selectedAccountSessions > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {formData.claims.length} / {selectedAccountSessions} citas
                    </Typography>
                  )}
                </Box>

                {!viewOnly && (
                  <>
                    <Button
                      onClick={addClaim}
                      startIcon={<AddIcon />}
                      variant="outlined"
                      sx={{ mb: 2 }}
                      disabled={(() => {
                        const sessionsAuthorized = isEditMode ? formData.sessionsAuthorized || 0 : selectedAccountSessions;
                        const sessionsRemaining = isEditMode ? formData.sessionsRemaining || 0 : selectedAccountSessions;
                        const currentClaimsCount = formData.claims.length;
                        const { canAdd } = canAddClaim(sessionsAuthorized, sessionsRemaining, currentClaimsCount, isEditMode);
                        return !canAdd;
                      })()}
                    >
                      {(() => {
                        const sessionsAuthorized = isEditMode ? formData.sessionsAuthorized || 0 : selectedAccountSessions;
                        const sessionsRemaining = isEditMode ? formData.sessionsRemaining || 0 : selectedAccountSessions;
                        const currentClaimsCount = formData.claims.length;
                        const { canAdd } = canAddClaim(sessionsAuthorized, sessionsRemaining, currentClaimsCount, isEditMode);

                        return canAdd ? "Agregar Cita" : "Límite alcanzado";
                      })()}
                    </Button>

                    {/* ✅ MENSAJE INFORMATIVO SIMPLE */}
                    {!isEditMode && selectedAccountSessions > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                        💡 Máximo {selectedAccountSessions} citas para esta cuenta
                      </Typography>
                    )}

                    {isEditMode && formData.sessionsRemaining === 0 && (
                      <Typography variant="caption" color="warning.main" sx={{ mb: 2, display: 'block' }}>
                        ⚠️ Sin sesiones restantes para nuevas citas
                      </Typography>
                    )}
                  </>
                )}

                {formData.claims.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    {viewOnly
                      ? 'No hay citas programadas para este registro.'
                      : 'No hay citas programadas. Haz clic en "Agregar Cita" para programar una.'
                    }
                  </Typography>
                ) : (
                  formData.claims.map((claim, idx) => renderClaimItem(claim, idx))
                )}
              </Box>
            </TabPanel>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: 'grey.50' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <Box>
              {!isFirstTab && (
                <Button onClick={prevTab} variant="outlined" sx={{ mr: 1 }}>
                  Anterior
                </Button>
              )}
              {!isLastTab && (
                <Button
                  onClick={nextTab}
                  variant="outlined"
                  disabled={!viewOnly && tabValue === 'client' && (!formData.firstName || !formData.lastName || !formData.accountId)}
                >
                  Siguiente
                </Button>
              )}
            </Box>

            {/* ✅ CAMBIO 4: CHECKBOX MEJORADO - Tooltip más claro */}
            {!viewOnly && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {(!isSyncedWithComPsych || hasNewClaims) && (
                  <Tooltip
                    title={
                      isSyncedWithComPsych
                        ? hasNewClaims
                          ? "Enviar las nuevas citas a ComPsych o solo guardarlas localmente"
                          : "No hay citas nuevas para sincronizar"
                        : sendToComPsych
                          ? formData.claims.length > 0
                            ? "Se enviará el referral con todas las citas a ComPsych (Estado: SINCRONIZADO)"
                            : "Se enviará el referral sin citas a ComPsych (Estado: SIN_CITAS)" // ✅ ACLARAR
                          : "Se guardará localmente sin enviar a ComPsych (Estado: PENDIENTE)"
                    }
                    arrow
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sendToComPsych}
                          onChange={(e) => setSendToComPsych(e.target.checked)}
                          color="primary"
                          disabled={loading || submitSuccessful || (isSyncedWithComPsych && !hasNewClaims)}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {isSyncedWithComPsych
                            ? "Sincronizar nuevas citas con ComPsych"
                            : "Enviar automáticamente a ComPsych"
                          }
                          {/* ✅ MEJORAR: Mostrar estado esperado */}
                          {sendToComPsych && !isSyncedWithComPsych && (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'info.main' }}>
                              {formData.claims.length > 0 ? "(→ Sincronizado)" : "(→ Sin Citas)"}
                            </Typography>
                          )}
                          {!sendToComPsych && !isSyncedWithComPsych && (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'warning.main' }}>
                              (→ Pendiente)
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                  </Tooltip>
                )}
              </Box>
            )}

            {/* ✅ NUEVO: Mensaje informativo para referrals sincronizados */}
            {isSyncedWithComPsych && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  icon={<CheckIcon />}
                  label="Ya sincronizado con ComPsych"
                  color="success"
                  variant="outlined"
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">
                  Solo se pueden agregar nuevas citas
                </Typography>
              </Box>
            )}

            <Box>
              <Button
                onClick={() => onClose(false)}
                color="inherit"
                sx={{ mr: 1 }}
                disabled={loading}
              >
                {viewOnly ? 'Cerrar' : 'Cancelar'}
              </Button>

              {!viewOnly && (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  color="primary"
                  disabled={isSubmitDisabled()}
                  startIcon={loading ? <CircularProgress size={20} /> : submitSuccessful ? <CheckIcon /> : <SaveIcon />}
                  sx={isSubmitDisabled() && !loading && !submitSuccessful ? { opacity: 0.6 } : {}}
                >
                  {getSubmitButtonText()}
                </Button>
              )}
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default CaptureModal;